import { headerAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "./logo.svg";

function instanceBaseUrl(instanceUrl?: string): string {
  if (!instanceUrl) return "";
  try {
    const url = new URL(instanceUrl);
    if (
      url.protocol !== "https:" ||
      url.username ||
      url.password ||
      url.search ||
      url.hash
    ) {
      return "";
    }
    return url.href.replace(/\/+$/, "");
  } catch {
    return "";
  }
}

export const opsmillInfrahub = defineConnector({
  identifier: "opsmillInfrahub",
  name: "OpsMill Infrahub",
  tagLine: "Infrastructure source of truth",
  keywords:
    "network networking automation graphql infrastructure opsmill infrahub",
  logo,
  protocol: "http",
  tags: ["it", "data", "engineering"],
  defaultUrlPattern: ({ instanceUrl }) => {
    const baseUrl = instanceBaseUrl(instanceUrl);
    if (!baseUrl) return "";
    const escaped = baseUrl.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return `^${escaped}/(?:api|graphql)(?:[/?].*)?$`;
  },
  auth: (app) =>
    headerAuth([{ name: "X-INFRAHUB-KEY", value: app.secret.apiToken }]),
  steps: [
    {
      title: "Create an account API token",
      items: [
        {
          kind: "markdown",
          content:
            "Ask your Infrahub administrator to issue an account API token. In Infrahub, open the user menu, select **Account settings > Tokens**, and click **+ Add Account Token**. Save the token details, then copy the generated token and store it securely. Use an account with only the permissions your workflows need. See [Managing API tokens](https://docs.infrahub.app/deploy-manage/user-management/managing-api-tokens).",
        },
      ],
    },
    {
      title: "Enter your Infrahub details",
      items: [
        {
          kind: "baseUrl",
          label: "Infrahub instance URL",
          required: true,
          placeholder: "https://infrahub.example.com",
          target: { kind: "configuration", field: "instanceUrl" },
          helperText:
            "The HTTPS base URL of your Infrahub deployment, including its port if needed. Do not include /api, /graphql, a query string, or the Schema Marketplace URL.",
        },
        {
          kind: "input",
          label: "API token",
          required: true,
          target: { kind: "secrets", field: "apiToken" },
          helperText:
            "Use a long-lived Infrahub account API token, not a short-lived JWT or a Schema Marketplace token. Replace this value when the token expires or is revoked.",
        },
        {
          kind: "markdown",
          content:
            "The connection test reads only your account’s ID through GraphQL. It does not require access to network objects or prove that your account can perform every workflow action.",
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: ({ instanceUrl }) => ({
      url: `${instanceBaseUrl(instanceUrl)}/graphql`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        query: "query GET_PROFILE_DETAILS { AccountProfile { id } }",
        operationName: "GET_PROFILE_DETAILS",
      }),
      expectedStatus: 200,
      expectedBodyContains: '"data":{"AccountProfile":{"id":"',
    }),
  },
  publicEnvVars: ({ instanceUrl }) => ({
    INFRAHUB_ADDRESS: instanceBaseUrl(instanceUrl),
  }),
  usageHint: ({ instanceUrl }) => {
    const baseUrl = instanceBaseUrl(instanceUrl);
    if (!baseUrl) return undefined;
    return `Use POST ${baseUrl}/graphql for the main branch or ${baseUrl}/graphql/<branch_name> for another branch. Send a JSON GraphQL query. The connector supplies X-INFRAHUB-KEY for this instance’s /graphql and /api paths; do not include the token yourself. Network object types and permissions depend on the instance’s schema and account.`;
  },
});
